import axios from 'axios';

// Use relative URL in development to work with proxy
const API_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'https://api.prepstop.com')
  : ''; // Empty string means use proxy in development

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const userManagementService = {
  // Users
  getAllUsers: async (filters?: { role?: string; partnerType?: string; search?: string }) => {
    const params = new URLSearchParams();
    if (filters?.role) params.append('role', filters.role);
    if (filters?.partnerType) params.append('partnerType', filters.partnerType);
    if (filters?.search) params.append('search', filters.search);
    
    const response = await axiosInstance.get(`/api/user-management/users?${params.toString()}`);
    return response.data;
  },

  createUser: async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    partnerType?: string;
    organizationName?: string;
    partnerId?: number;
    tenantId?: number;
  }) => {
    const response = await axiosInstance.post('/api/user-management/users', userData);
    return response.data;
  },

  createUserWithImage: async (formData: FormData) => {
    const response = await axios.post(`${API_URL}/api/user-management/users`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      withCredentials: true
    });
    return response.data;
  },

  getUserById: async (id: number) => {
    const response = await axiosInstance.get(`/api/user-management/users/${id}`);
    return response.data;
  },

  updateUser: async (id: number, userData: any) => {
    const response = await axiosInstance.put(`/api/user-management/users/${id}`, userData);
    return response.data;
  },

  updateUserWithImage: async (id: number, formData: FormData) => {
    const response = await axios.put(`${API_URL}/api/user-management/users/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      withCredentials: true
    });
    return response.data;
  },

  deleteUser: async (id: number) => {
    const response = await axiosInstance.delete(`/api/user-management/users/${id}`);
    return response.data;
  },

  // Get markets already assigned to pharmacy partners
  getAssignedMarkets: async (excludeUserId?: string) => {
    const params = excludeUserId ? `?excludeUserId=${excludeUserId}` : '';
    const response = await axiosInstance.get(`/api/user-management/assigned-markets${params}`);
    return response.data;
  },

  // Patients
  getAllPatients: async (filters?: {
    market?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.market) params.append('market', filters.market);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    const response = await axiosInstance.get(`/api/user-management/patients?${params.toString()}`);
    return response.data;
  },

  getPatientById: async (id: number) => {
    const response = await axiosInstance.get(`/api/user-management/patients/${id}`);
    return response.data;
  },

  // Dashboard
  getDashboardStats: async () => {
    const response = await axiosInstance.get('/api/user-management/dashboard');
    return response.data;
  },

  // Ambassador statistics
  getAmbassadorStats: async (ambassadorId: number) => {
    const response = await axiosInstance.get(`/api/user-management/ambassadors/${ambassadorId}/stats`);
    return response.data;
  },

  // Pharmacy statistics
  getPharmacyStats: async (pharmacyId: number) => {
    const response = await axiosInstance.get(`/api/user-management/pharmacy/${pharmacyId}/stats`);
    return response.data;
  },
};
